import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'

/**
 * The words this module returns, and the locale it formats dates in.
 *
 * REQUIRED, not defaulted. Every string below used to be an English literal
 * in this file and rendered verbatim on a Korean dashboard
 * (`초안 · No proof · 1시간 전`, appliance profile, 2026-08-19). A defaulted
 * `copy = EN_…` parameter — the shape `summarizeShareHealth` uses next door —
 * would let a new call site fall back to English in silence, which is exactly
 * how this defect would return. Required means the compiler names every
 * caller that has not been given a locale.
 *
 * Structurally satisfied by `useStudioLocale()`, so a component passes what
 * it already has: `getProjectProofHealth(useStudioLocale(), project)`.
 */
export interface ProjectProofHealthCopySource {
  locale: LocaleCode
  strings: Pick<StudioDictionary, 'proofHealth'>
}

export type ProjectProofHealthRecordKind =
  | 'production-studio-readback'
  | 'browser-render-proof'
  | 'design-system-proof'
  | 'cleanup-proof'

export type ProjectProofHealthRecordStatus = 'pass' | 'review' | 'blocked'

export interface ProjectProofHealthRecord {
  id: string
  kind: ProjectProofHealthRecordKind
  status: ProjectProofHealthRecordStatus
  title: string
  summary: string
  source: string
  createdAt: string
  missing: string[]
  failureReasons: string[]
}

export type ProjectProofHealthStatus = 'verified' | 'review' | 'blocked' | 'none'
export type ProjectProofHealthTone = 'ready' | 'review' | 'blocked' | 'muted'

/**
 * What the next-action control should do — a value, not a sentence.
 *
 * This replaces the `actionLabel` string this module used to return. Its ONLY
 * consumer (`apps-page.tsx :: proofActionLabel`) branched on that string with
 * `/deploy/i.test(label)`, so the moment the label was translated the branch
 * went silently dead and the raw label reached the button. The consumer now
 * switches on this discriminator and reads its own localized words, which is
 * why translating the copy cannot change behaviour.
 */
export type ProjectProofHealthAction = 'review-evidence' | 'open-deploy'

export interface ProjectProofHealth {
  status: ProjectProofHealthStatus
  tone: ProjectProofHealthTone
  label: string
  detail: string
  action: ProjectProofHealthAction
  latestRecord: ProjectProofHealthRecord | null
  lastVerifiedAt: string | null
  blockingReason: string | null
  records: ProjectProofHealthRecord[]
}

export function getProjectProofHealth(
  source: ProjectProofHealthCopySource,
  ...sources: unknown[]
): ProjectProofHealth {
  const copy = source.strings.proofHealth
  const records = mergeProofRecords(sources.flatMap(readProjectProofHealthRecords))
  const latestReadback = records
    .filter((record) => record.kind === 'production-studio-readback')
    .sort(sortByCreatedAtDesc)[0] ?? null

  if (latestReadback) {
    if (latestReadback.status === 'pass') {
      return {
        status: 'verified',
        tone: 'ready',
        label: copy.verified,
        detail: copy.checkedLive(formatProofDate(latestReadback.createdAt, source.locale, copy)),
        action: 'review-evidence',
        latestRecord: latestReadback,
        lastVerifiedAt: latestReadback.createdAt,
        blockingReason: null,
        records,
      }
    }

    if (latestReadback.status === 'blocked') {
      return {
        status: 'blocked',
        tone: 'blocked',
        label: copy.blocked,
        detail: firstIssue(latestReadback) ?? copy.liveCheckBlocked,
        action: 'open-deploy',
        latestRecord: latestReadback,
        lastVerifiedAt: null,
        blockingReason: firstIssue(latestReadback) ?? latestReadback.summary,
        records,
      }
    }

    return {
      status: 'review',
      tone: 'review',
      label: copy.needsReview,
      detail: latestReadback.summary || copy.liveCheckNeedsReview,
      action: 'review-evidence',
      latestRecord: latestReadback,
      lastVerifiedAt: null,
      blockingReason: null,
      records,
    }
  }

  const blockedRecord = records.find((record) => record.status === 'blocked') ?? null
  if (blockedRecord) {
    return {
      status: 'blocked',
      tone: 'blocked',
      label: copy.blocked,
      detail: firstIssue(blockedRecord) ?? copy.recordBlocked,
      action: 'open-deploy',
      latestRecord: blockedRecord,
      lastVerifiedAt: null,
      blockingReason: firstIssue(blockedRecord) ?? blockedRecord.summary,
      records,
    }
  }

  const reviewRecord = records.find((record) => record.status === 'review') ?? null
  if (reviewRecord) {
    return {
      status: 'review',
      tone: 'review',
      label: copy.needsReview,
      detail: reviewRecord.summary || copy.recordNeedsReview,
      action: 'review-evidence',
      latestRecord: reviewRecord,
      lastVerifiedAt: null,
      blockingReason: null,
      records,
    }
  }

  const supportingProof = records.find((record) => record.status === 'pass') ?? null
  if (supportingProof) {
    return {
      status: 'review',
      tone: 'review',
      label: copy.needsLiveCheck,
      detail: copy.openDeployToRun,
      action: 'open-deploy',
      latestRecord: supportingProof,
      lastVerifiedAt: null,
      blockingReason: null,
      records,
    }
  }

  return {
    status: 'none',
    tone: 'muted',
    label: copy.none,
    detail: copy.openDeployToRun,
    action: 'open-deploy',
    latestRecord: null,
    lastVerifiedAt: null,
    blockingReason: null,
    records,
  }
}

export function readProjectProofHealthRecords(source: unknown): ProjectProofHealthRecord[] {
  const record = asRecord(source)
  const metadata = asRecord(record.metadata)
  return mergeProofRecords([
    ...normalizeProofRecordArray(record.proofRecords),
    ...normalizeProofRecordArray(record.libraryProofRecords),
    ...normalizeProofRecordArray(metadata.proofRecords),
    ...normalizeProofRecordArray(metadata.libraryProofRecords),
  ])
}

function normalizeProofRecordArray(value: unknown): ProjectProofHealthRecord[] {
  if (!Array.isArray(value)) return []
  return value.map(normalizeProofRecord).filter((record): record is ProjectProofHealthRecord => Boolean(record))
}

function normalizeProofRecord(value: unknown): ProjectProofHealthRecord | null {
  const record = asRecord(value)
  const id = firstString(record.id)
  const kind = normalizeKind(record.kind)
  const status = normalizeStatus(record.status)
  const title = firstString(record.title)
  const source = firstString(record.source)
  const createdAt = firstString(record.createdAt)
  if (!id || !kind || !status || !title || !source || !createdAt) return null

  return {
    id,
    kind,
    status,
    title,
    summary: firstString(record.summary) || title,
    source,
    createdAt,
    missing: stringArray(record.missing),
    failureReasons: stringArray(record.failureReasons),
  }
}

function normalizeKind(value: unknown): ProjectProofHealthRecordKind | null {
  return value === 'production-studio-readback'
    || value === 'browser-render-proof'
    || value === 'design-system-proof'
    || value === 'cleanup-proof'
    ? value
    : null
}

function normalizeStatus(value: unknown): ProjectProofHealthRecordStatus | null {
  return value === 'pass' || value === 'review' || value === 'blocked' ? value : null
}

function mergeProofRecords(records: ProjectProofHealthRecord[]): ProjectProofHealthRecord[] {
  const byId = new Map<string, ProjectProofHealthRecord>()
  for (const record of records) {
    byId.set(record.id, record)
  }
  return Array.from(byId.values()).sort(sortByCreatedAtDesc)
}

function sortByCreatedAtDesc(a: ProjectProofHealthRecord, b: ProjectProofHealthRecord): number {
  return b.createdAt.localeCompare(a.createdAt)
}

function firstIssue(record: ProjectProofHealthRecord): string | null {
  return record.failureReasons[0] ?? record.missing[0] ?? null
}

function formatProofDate(
  value: string,
  locale: LocaleCode,
  copy: StudioDictionary['proofHealth'],
): string {
  const timestamp = new Date(value).getTime()
  if (!Number.isFinite(timestamp)) return copy.dateUnrecorded
  // Was `toLocaleDateString('ko-KR')` — a Korean date under every locale,
  // including English. `overview-page.tsx` already formats with the active
  // `LocaleCode`; this follows it.
  return new Date(timestamp).toLocaleDateString(locale)
}

function stringArray(value: unknown): string[] {
  if (typeof value === 'string' && value.trim()) return [value.trim()]
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return undefined
}
